"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import { API_URL } from "../config"
import { useLanguage } from "../context/LanguageContext"
import { useNotification } from "../context/NotificationContext"
import "./CropManagement.css"

const CropManagement = () => {
  const { t, direction } = useLanguage()
  const { showNotification } = useNotification()
  const [crops, setCrops] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [currentCrop, setCurrentCrop] = useState(null)

  // Form state
  const [formData, setFormData] = useState({
    cropType: "",
    crop: "",
    growthStages: {
      initial: 0,
      development: 0,
      mid: 0,
      late: 0,
    },
    total: 0,
    plantDate: "",
    region: "",
    cropCoefficients: {
      initial: 0.3,
      mid: 1.0,
      end: 0.7,
    },
  })

  // Fetch crops on component mount
  useEffect(() => {
    fetchCrops()
  }, [])

  const fetchCrops = async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await axios.get(`${API_URL}/api/crops`)
      setCrops(res.data)
      setLoading(false)
    } catch (err) {
      console.error("Error fetching crops:", err)
      setError("Failed to fetch crops")
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target

    if (name.includes(".")) {
      const [parent, child] = name.split(".")
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value,
        },
      })
    } else {
      setFormData({
        ...formData,
        [name]: value,
      })
    }

    // Auto-calculate total when growth stages change
    if (name.startsWith("growthStages.")) {
      const { initial, development, mid, late } = {
        ...formData.growthStages,
        [name.split(".")[1]]: Number.parseInt(value) || 0,
      }

      const total = initial + development + mid + late
      setFormData((prev) => ({
        ...prev,
        total,
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      if (isEditing && currentCrop) {
        await axios.put(`${API_URL}/api/crops/${currentCrop._id}`, formData)
        showNotification(t("Crop updated successfully"), "success")
      } else {
        await axios.post(`${API_URL}/api/crops`, formData)
        showNotification(t("Crop added successfully"), "success")
      }

      // Reset form and fetch updated crops
      resetForm()
      fetchCrops()
    } catch (err) {
      console.error("Error saving crop:", err)
      const errorMsg = err.response?.data?.msg || "An error occurred"
      showNotification(t(errorMsg), "error")
    }
  }

  const handleEdit = (crop) => {
    setIsEditing(true)
    setCurrentCrop(crop)
    setFormData({
      cropType: crop.cropType,
      crop: crop.crop,
      growthStages: {
        initial: crop.growthStages.initial,
        development: crop.growthStages.development,
        mid: crop.growthStages.mid,
        late: crop.growthStages.late,
      },
      total: crop.total,
      plantDate: new Date(crop.plantDate).toISOString().split("T")[0],
      region: crop.region,
      cropCoefficients: {
        initial: crop.cropCoefficients.initial,
        mid: crop.cropCoefficients.mid,
        end: crop.cropCoefficients.end,
      },
    })
    setShowForm(true)
  }

  const handleDelete = async (id) => {
    if (!window.confirm(t("Are you sure you want to delete this crop?"))) {
      return
    }

    try {
      await axios.delete(`${API_URL}/api/crops/${id}`)
      showNotification(t("Crop deleted successfully"), "success")
      fetchCrops()
    } catch (err) {
      console.error("Error deleting crop:", err)
      const errorMsg = err.response?.data?.msg || "An error occurred"
      showNotification(t(errorMsg), "error")
    }
  }

  const resetForm = () => {
    setFormData({
      cropType: "",
      crop: "",
      growthStages: {
        initial: 0,
        development: 0,
        mid: 0,
        late: 0,
      },
      total: 0,
      plantDate: "",
      region: "",
      cropCoefficients: {
        initial: 0.3,
        mid: 1.0,
        end: 0.7,
      },
    })
    setIsEditing(false)
    setCurrentCrop(null)
    setShowForm(false)
  }

  const toggleForm = () => {
    if (showForm) {
      resetForm()
    } else {
      setShowForm(true)
    }
  }

  return (
    <div className="crop-management-container" dir={direction}>
      <div className="crop-management-header">
        <h1>{t("Crop Management")}</h1>
        <p className="subtitle">{t("Manage crop data for WATref and Kc calculations")}</p>
        <button className="toggle-form-button" onClick={toggleForm}>
          {showForm ? t("Cancel") : t("Add New Crop")}
        </button>
      </div>

      {showForm && (
        <div className="crop-form-container">
          <div className="card">
            <div className="card-header">
              <h2>{isEditing ? t("Edit Crop") : t("Add New Crop")}</h2>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit} className="crop-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="cropType">{t("Crop Type")}</label>
                    <input
                      type="text"
                      id="cropType"
                      name="cropType"
                      value={formData.cropType}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="crop">{t("Crop")}</label>
                    <input
                      type="text"
                      id="crop"
                      name="crop"
                      value={formData.crop}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <h3>{t("Growth Stages (days)")}</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="growthStages.initial">{t("Init. (Lini)")}</label>
                    <input
                      type="number"
                      id="growthStages.initial"
                      name="growthStages.initial"
                      value={formData.growthStages.initial}
                      onChange={handleInputChange}
                      min="0"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="growthStages.development">{t("Dev. (Ldev)")}</label>
                    <input
                      type="number"
                      id="growthStages.development"
                      name="growthStages.development"
                      value={formData.growthStages.development}
                      onChange={handleInputChange}
                      min="0"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="growthStages.mid">{t("Mid (Lmid)")}</label>
                    <input
                      type="number"
                      id="growthStages.mid"
                      name="growthStages.mid"
                      value={formData.growthStages.mid}
                      onChange={handleInputChange}
                      min="0"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="growthStages.late">{t("Late (Llate)")}</label>
                    <input
                      type="number"
                      id="growthStages.late"
                      name="growthStages.late"
                      value={formData.growthStages.late}
                      onChange={handleInputChange}
                      min="0"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="total">{t("Total")}</label>
                    <input
                      type="number"
                      id="total"
                      name="total"
                      value={formData.total}
                      onChange={handleInputChange}
                      min="0"
                      required
                      readOnly
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="plantDate">{t("Plant Date")}</label>
                    <input
                      type="date"
                      id="plantDate"
                      name="plantDate"
                      value={formData.plantDate}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="region">{t("Region")}</label>
                    <input
                      type="text"
                      id="region"
                      name="region"
                      value={formData.region}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <h3>{t("Crop Coefficients")}</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="cropCoefficients.initial">{t("Kc ini")}</label>
                    <input
                      type="number"
                      id="cropCoefficients.initial"
                      name="cropCoefficients.initial"
                      value={formData.cropCoefficients.initial}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      max="2"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="cropCoefficients.mid">{t("Kc mid")}</label>
                    <input
                      type="number"
                      id="cropCoefficients.mid"
                      name="cropCoefficients.mid"
                      value={formData.cropCoefficients.mid}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      max="2"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="cropCoefficients.end">{t("Kc end")}</label>
                    <input
                      type="number"
                      id="cropCoefficients.end"
                      name="cropCoefficients.end"
                      value={formData.cropCoefficients.end}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      max="2"
                      required
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="cancel-button" onClick={resetForm}>
                    {t("Cancel")}
                  </button>
                  <button type="submit" className="submit-button">
                    {isEditing ? t("Update") : t("Add")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="crops-list-container">
        <div className="card">
          <div className="card-header">
            <h2>{t("Crops List")}</h2>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>{t("Loading crops...")}</p>
              </div>
            ) : error ? (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i> {t(error)}
              </div>
            ) : crops.length === 0 ? (
              <p className="no-data">{t("No crops found. Add your first crop to get started.")}</p>
            ) : (
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t("Crop Type")}</th>
                      <th>{t("Crop")}</th>
                      <th>{t("Region")}</th>
                      <th>{t("Total Days")}</th>
                      <th>{t("Plant Date")}</th>
                      <th>{t("Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {crops.map((crop) => (
                      <tr key={crop._id}>
                        <td>{crop.cropTypeDetails.name}</td>
                        <td>{crop.name}</td>
                        <td>{crop.region}</td>
                        <td>{crop.totalDays}</td>
                        <td>{new Date(crop.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div className="table-actions">
                            <button
                              className="view-button"
                              onClick={() => (window.location.href = `/kc-calculator/${crop._id}`)}
                            >
                              <i className="fas fa-chart-line"></i>
                            </button>
                            <button className="edit-button" onClick={() => handleEdit(crop)}>
                              <i className="fas fa-edit"></i>
                            </button>
                            <button className="delete-button" onClick={() => handleDelete(crop._id)}>
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default CropManagement
